import { pick } from 'ramda'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { useOrg } from '@/hooks'
import useValidateEndpoint from '@/hooks/useValidate'
import { ConfigDataScreen } from '@/screens/ComponentLibrary/components'
import { useGetCustomComponent } from '@/screens/ComponentLibrary/hooks'
import { parseComponentTypes } from '@/utils'
import { ComponentType } from '@/utils/api'

import { IntroductionSettings } from '../../types/customComponents'

export const parseIntroductionData = (
  orgSlug?: string,
  param?: { settings: IntroductionSettings } | string | null
) => {
  if (!orgSlug || !param) return {}
  const data = parseComponentTypes(param, ComponentType.introduction)
  if (!data) return {}
  const filteredSettings = pick(
    [
      'IntroText',
      'IntroButtonText',
      'IncludeTermsAgreement',
      'ConsentText',
      'ConsentButtonText',
    ],
    data.settings
  )
  if (typeof filteredSettings.IntroText?.en === 'string')
    return {
      error: 'Out of date',
      message: 'Please save the component to update to the latest version',
    }

  return {
    data: filteredSettings,
    organization: orgSlug,
    schemaName: 'IntroConfig',
    schemaVersion: '1.0.0',
    version: '1.0.0',
  }
}

export const useParseIntroductionData = (
  orgSlug?: string,
  param?: { settings: IntroductionSettings } | string | null
) => {
  return useMemo(() => {
    return parseIntroductionData(orgSlug, param)
  }, [orgSlug, param])
}

export const ConfigData = () => {
  const { orgID } = useParams()
  const { componentID } = useComponentProvider()
  const org = useOrg(orgID)
  const current = useGetCustomComponent<IntroductionSettings>(componentID)

  const config = useParseIntroductionData(org.data?.slug, current.data)
  const valid = useValidateEndpoint(config, componentID)

  return (
    <ConfigDataScreen
      config={config}
      current={current}
      org={org}
      rootName="Introduction"
      valid={valid}
    />
  )
}
